import metaModel from '~/shared/src/utilities/metaModel';
import config from '~/shared/src/config/modules/finder';
import {ItemAttributes} from '~/shared/src/ci/Item';
import {ReferenceAttributes} from '~/shared/src/ci/Reference';
import User from '~/shared/src/model/User';
import {
    loadDataParameterDefaults,
    LoadDataParameters
} from '~/shared/src/utilities/modules/datatable';
import {
    DataObjectProperties,
    DataObjectReferenceProperties,
    DataStructure,
    DataDescriptor,
    getDataObject,
    buildDataDescriptor,
    ObjectDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {
    LoadMetaDataRowData,
    LoadMetaDataRowDataMapping
} from '~/shared/src/utilities/modules/overview';
import {
    ColumnDefinition,
    ColumnFilter,
    ColumnSorter,
    ConfigDescriptor
} from '~/shared/src/validationSchemes/modules/overview';
import {BaseRequestParameters} from '~/shared/src/model/BaseRequest';


/*** type definitions ************************************************************************************************/

export type LoadFinderOverviewDataParameters = LoadDataParameters & {
    itemTypeName : string;
};
export const loadFinderOverviewDataParameterDefaults = loadDataParameterDefaults;
export type LoadFinderOverviewDataResult = {
    success : boolean;
    data : {
        items : ( Required<Pick<ItemAttributes, 'id'>> & ItemAttributes )[];
        references : ( Required<Pick<ReferenceAttributes, 'id'>> & ReferenceAttributes )[];
    };
    metaData : {
        rowData : LoadMetaDataRowData[];
        rowDataMapping : LoadMetaDataRowDataMapping;
        total : number;
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};


export type LoadFinderExportDataParameters = {
    itemTypeName : string;
    searchString? : string;
    filters? : ColumnFilter[];
    propertySets? : number[] | '*';
    referenceSets? : number[] | '*';
};
export const loadFinderExportDataParameterDefaults = {
    filters      : [],
    propertySets : [],
    referenceSets: []
};
export type LoadFinderExportDataResult = {
    success : boolean;
    data : {
        dataXml : string;
    };
    metaData : {
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};


export type LoadReferencePanelDataParameters = {
    parentItemId : number;
    itemTypeName : string;
    parentItemTypeName : string;
    columns? : string[];
    filters? : ColumnFilter[];
    sorters? : ColumnSorter[];
    start? : number;
    limit? : number;
};
export const loadReferencePanelDataParameterDefaults = {
    columns: [],
    filters: [],
    sorters: [],
    start  : 0,
    limit  : 100
};
export type LoadReferencePanelDataResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
        references : ReferenceAttributes[];
    };
    metaData : {
        rowData : LoadMetaDataRowData[];
        rowDataMapping : LoadMetaDataRowDataMapping;
        total : number;
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};


export type LoadDetailDataParameters = Partial<BaseRequestParameters> & {
    itemId : number;
    itemType? : string;
    itemKey? : string;
};
export type LoadDetailDataResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
        references : ReferenceAttributes[];
    };
    metaData : {
        referenceCounts : {
            dest : { [ referenceSetId : number ] : number };
            src : { [ referenceSetId : number ] : number }
        };
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        srcRefCountCypher? : string;
        srcRefCountSql? : string;
        destRefCountCypher? : string;
        destRefCountSql? : string;
        parameters? : { [ parameter : string ] : any };
    };
}

export type LoadReferencesForExportParameters = {
    itemId : number;
    referenceSetIds : {
        dest : number[];
        src : number[];
    }
};
export type LoadReferencesForExportResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
        references : ReferenceAttributes[];
    };
    metaData : {
        metrics : Metric[];

        // debug
        srcReferenceCypher? : string;
        srcReferenceSql? : string;
        srcReferenceParameters? : { [ parameter : string ] : any };
        destReferenceCypher? : string;
        destReferenceSql? : string;
        destReferenceParameters? : { [ parameter : string ] : any };
    };
}

export type GetRootItemIdByDisplayAttributeParameters = {
    itemTypeId : number;
    key? : string;
    name? : string;
};
export type GetRootItemIdByDisplayAttributeResult = {
    success : true;
    data : {
        rootItemId? : number;
        status? : string;
    };
    metaData : {
        metrics : Metric[];

        // debug
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

export type LoadItemHistoryDataParameters = {
    itemId : number;
    filters? : ColumnFilter[];
    sorters? : ColumnSorter[];
    start? : number;
    limit? : number;
};
export type LoadItemHistoryDataResult = {
    success : boolean;
    data : {
        items : ( Required<Pick<ItemAttributes, 'id'>> & ItemAttributes )[];
        references : ( Required<Pick<ReferenceAttributes, 'id'>> & ReferenceAttributes )[];
    };
    metaData : {
        rowData : LoadMetaDataRowData[];
        rowDataMapping : LoadMetaDataRowDataMapping;
        total : number;
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

export type LoadPropertiesHistoryDataParameters = {
    itemId : number;
    filters? : ColumnFilter[];
    sorters? : ColumnSorter[];
    start? : number;
    limit? : number;
};
export type LoadPropertiesHistoryDataResult = {
    success : boolean;
    data : {
        items : ( Required<Pick<ItemAttributes, 'id'>> & ItemAttributes )[];
        references : ( Required<Pick<ReferenceAttributes, 'id'>> & ReferenceAttributes )[];
    };
    metaData : {
        rowData : LoadMetaDataRowData[];
        rowDataMapping : LoadMetaDataRowDataMapping;
        total : number;
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

export type LoadReferencesHistoryDataParameters = {
    itemId : number;
    filters? : ColumnFilter[];
    sorters? : ColumnSorter[];
    start? : number;
    limit? : number;
};
export type LoadReferencesHistoryDataResult = {
    success : boolean;
    data : {
        items : ( Required<Pick<ItemAttributes, 'id'>> & ItemAttributes )[];
        references : ( Required<Pick<ReferenceAttributes, 'id'>> & ReferenceAttributes )[];
    };
    metaData : {
        rowData : LoadMetaDataRowData[];
        rowDataMapping : LoadMetaDataRowDataMapping;
        total : number;
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

export type LoadReferencePropertiesHistoryDataParameters = {
    itemId : number;
    filters? : ColumnFilter[];
    sorters? : ColumnSorter[];
    start? : number;
    limit? : number;
};
export type LoadReferencePropertiesHistoryDataResult = {
    success : boolean;
    data : {
        items : ( Required<Pick<ItemAttributes, 'id'>> & ItemAttributes )[];
        references : ( Required<Pick<ReferenceAttributes, 'id'>> & ReferenceAttributes )[];
    };
    metaData : {
        rowData : LoadMetaDataRowData[];
        rowDataMapping : LoadMetaDataRowDataMapping;
        total : number;
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};


/*** config definitions ********************************************************************************************/

export const valuePrecision = 2;


/*** function definitions ********************************************************************************************/

/**
 * Is the given timestamp resulting in a history search.
 *
 * @param {String} timestamp
 *
 * @return {Boolean}
 */
export const isHistorySearch = ( timestamp : string ) : boolean => {
    return timestamp !== 'now';
}

/**
 * Gets the targetArea from the given status and the given timestamp.
 *
 * @param {'all'|'all-non-history'|'active'|'planned'|'template'|'history'|'deleted'|'active-planned'|'active-planned-template'|'imported'} status
 * @param {String} timestamp
 *
 * @return {'all'|'active'}
 */
export const getTargetArea = ( status : Status, timestamp : string ) : 'all' | 'active' => {
    return status === 'all' || timestamp !== 'now'
        ? 'all'
        : 'active';
}

export const convertItemTypeId = ( id : number ) : number => {
    return 1000000 + id;
}

export const convertReferenceSetId = ( id : number ) : number => {
    if ( id < 0 ) {
        return -2000000 + id;
    }
    else {
        return 2000000 + id;
    }
}

/**
 * Builds the dataStructure
 *
 * @param {String} schema
 * @param {String} itemTypeName
 * @param {User} user
 *
 * @return {DataStructure}
 */
export const getDataStructure = ( schema : Schema, itemTypeName : string, user : User ) : DataStructure => {
    const retVal : DataStructure = {
              id    : '0',
              origId: 0,
              item  : {
                  id  : 0,
                  key : 'noItemType',
                  name: 'noItemType'
              }
          },
          itemTypeId             = metaModel[ schema ].getItemType( itemTypeName )?.id;

    if ( itemTypeId ) {
        retVal.id = String( itemTypeId );
        retVal.origId = itemTypeId;
        retVal.item.id = itemTypeId;
        retVal.item.key = itemTypeName;
        retVal.item.name = itemTypeName;
        retVal.item.objectSchema = schema;
        retVal.item.objectType = itemTypeName;

        const referenceSets = metaModel[ schema ].getReferenceSetsForItemType( itemTypeId );

        if ( referenceSets.length ) {
            retVal.dataStructure = [];

            const circularReferenceSetsHandled : number[] = [];

            // build columnDefinitions
            for ( const referenceSet of referenceSets ) {
                const referenceEnd       = referenceSet.srcItemTypeId === itemTypeId && !circularReferenceSetsHandled.includes( referenceSet.id )
                          ? 'dest'
                          : 'src',
                      referenceDirection = referenceSet.srcItemTypeId === itemTypeId && !circularReferenceSetsHandled.includes( referenceSet.id )
                          ? 'DestToSrc'
                          : 'SrcToDest';

                if (
                    (
                        !config.loadOnlyToOneReferences
                        || referenceSet.cardinality === '1:1'
                        || ( referenceEnd === 'dest' && referenceSet.cardinality === 'n:1' )
                        || ( referenceEnd === 'src' && referenceSet.cardinality === '1:n' )
                    )
                    && user.hasPermissionOnReferenceSet( 'read', referenceSet.id, schema )
                    && user.hasGenericPermissionOnReferenceSet( 'read', referenceSet.id, schema )
                ) {
                    const id                                                                                 = convertReferenceSetId( !circularReferenceSetsHandled.includes( referenceSet.id )
                              ? referenceSet.id
                              : -referenceSet.id ),
                          targetItemType                                                                     = referenceSet.srcItemTypeId === itemTypeId && !circularReferenceSetsHandled.includes( referenceSet.id )
                              ? referenceSet.destItemType
                              : referenceSet.srcItemType,
                          key                                                                                = `ref_${targetItemType.name}_${referenceSet.referenceType.name}${id < 0 ? '_reverse' : ''}`,
                          referenceConfiguration : DataObjectReferenceProperties[ 'referenceConfiguration' ] = {};

                    referenceConfiguration[ referenceSet.referenceType.name ] = {
                        dontIncludeAllProperties: true,
                        referenceDirection
                    };

                    if ( referenceSet.srcItemTypeId === referenceSet.destItemTypeId && !circularReferenceSetsHandled.includes( referenceSet.id ) ) {
                        circularReferenceSetsHandled.push( referenceSet.id )
                    }

                    retVal.dataStructure.push( {
                        id    : String( id ),
                        origId: referenceSet.id,

                        item: {
                            id                      : referenceSet.id,
                            key                     : key,
                            name                    : targetItemType.name,
                            dontIncludeAllProperties: true,
                            objectSchema            : schema,
                            objectType              : targetItemType.name
                        },

                        reference: {
                            id: referenceSet.id,
                            referenceConfiguration
                        }
                    } );
                }
            }
        }
    }

    return retVal;
}

/**
 * Builds the initial sorters
 *
 * @param {DataObjectProperties} dataObject
 * @param {String} schema
 * @param {String} itemTypeName
 *
 * @return {ColumnSorter[]}
 */
export const getInitialSorters = ( dataObject : DataObjectProperties, schema : Schema, itemTypeName : string ) : ColumnSorter[] => {
    const retVal : ColumnSorter[] = [];

    if ( itemTypeName !== '' ) {
        const displayField = metaModel[ schema ].getItemType( itemTypeName )?.displayAttribute;

        if ( displayField ) {
            const dataDescriptor = buildDataDescriptor( { attributePath: `item.${displayField}` }, dataObject ) as ObjectDataDescriptor;

            retVal.push( {
                key      : dataDescriptor.alias,
                field    : dataDescriptor.alias,
                direction: 'ASC' as SortDirection
            } );
        }
    }

    if ( !retVal.length ) {
        const dataDescriptor = buildDataDescriptor( { attributePath: 'item.key' }, dataObject ) as ObjectDataDescriptor;

        retVal.push( {
            key      : dataDescriptor.alias,
            field    : dataDescriptor.alias,
            direction: 'ASC' as SortDirection
        } );
    }

    return retVal;
}

/**
 * Extracts and returns the config attributes from the given column definition.
 *
 * @param {Object} columnDefinition
 *
 * @return {Object}
 */
export const getConfigAttributesForColumnDefinition = ( columnDefinition : ConfigDescriptor & Object ) : ConfigDescriptor => {
    const retVal : ConfigDescriptor = {};

    if ( columnDefinition ) {
        retVal.width = columnDefinition.width;

        if ( columnDefinition.align && columnDefinition.align !== 'left' ) {
            retVal.align = columnDefinition.align;
        }
        if ( columnDefinition.resizeable !== undefined ) {
            retVal.resizeable = columnDefinition.resizeable;
        }
        else {
            if ( columnDefinition.minWidth ) {
                retVal.minWidth = columnDefinition.minWidth;
            }
            if ( columnDefinition.maxWidth ) {
                retVal.maxWidth = columnDefinition.maxWidth;
            }
        }
        if ( columnDefinition.format ) {
            retVal.format = columnDefinition.format;
        }
        if ( columnDefinition.frozen ) {
            retVal.frozen = columnDefinition.frozen;
        }
        if ( columnDefinition.styling ) {
            retVal.styling = columnDefinition.styling;
        }
    }

    return retVal;
}

/**
 * Creates the column definitions for item attributes
 *
 * @param {DataStructure} dataStructure
 *
 * @return {ColumnDefinition[]}
 */
export const getItemAttributeColumnDefinitions = ( dataStructure : DataStructure ) : ColumnDefinition[] => {
    const retVal : ColumnDefinition[] = [];

    let columnDataDescriptor : DataDescriptor;

    // id
    columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.id' }, dataStructure ) as ObjectDataDescriptor;
    retVal.push( {
        key           : columnDataDescriptor.alias,
        field         : columnDataDescriptor.alias,
        header        : 'YN.base.finder.item.idLabel',
        dataDescriptor: columnDataDescriptor,
        permissions   : {},
        editor        : false,
        filter        : true,
        reorderable   : false,
        hidable       : false,
        resizeable    : false,
        width         : 100
    } );

    // key
    columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.key' }, dataStructure ) as ObjectDataDescriptor;
    retVal.push( {
        key           : columnDataDescriptor.alias,
        field         : columnDataDescriptor.alias,
        header        : 'item.key',
        dataDescriptor: columnDataDescriptor,
        permissions   : {},
        editor        : true,
        filter        : true,
        width         : 300,
        minWidth      : 100
    } );

    // name
    columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.name' }, dataStructure ) as ObjectDataDescriptor;
    retVal.push( {
        key           : columnDataDescriptor.alias,
        field         : columnDataDescriptor.alias,
        header        : 'item.name',
        dataDescriptor: columnDataDescriptor,
        permissions   : {},
        editor        : true,
        filter        : true,
        width         : 300,
        minWidth      : 100
    } );

    return retVal;
}

/**
 * Creates the column dataDescriptor for ItemType attribute
 *
 * @param {DataStructure} dataStructure
 *
 * @return {DataDescriptor}
 */
export const getItemTypeNameColumnDataDescriptor = ( dataStructure : DataStructure ) : DataDescriptor => {
    return buildDataDescriptor( { attributePath: 'item.itemTypeName' }, dataStructure ) as ObjectDataDescriptor;
}

/**
 * Creates the column definitions for references
 *
 * @param {DataStructure} dataStructure
 * @param {String} schema
 * @param {String} itemTypeName
 * @param {User} user
 *
 * @return {ColumnDefinition[]}
 */
export const getReferenceColumnDefinitions = ( dataStructure : DataStructure, schema : Schema, itemTypeName : string, user : User ) : ColumnDefinition[] => {
    const retVal : ColumnDefinition[] = [];

    if ( itemTypeName !== '' ) {
        const referenceSets = metaModel[ schema ].getReferenceSetsForItemType( itemTypeName );

        if ( referenceSets.length ) {

            // order by target ItemTypeName and ReferenceTypeName
            const itemTypeId                              = metaModel[ schema ].getItemType( itemTypeName )?.id,
                  circularReferenceSetsHandled : number[] = [];

            // build columnDefinitions
            for ( const referenceSet of referenceSets ) {
                const targetItemIs   = referenceSet.srcItemTypeId === itemTypeId && !circularReferenceSetsHandled.includes( referenceSet.id )
                          ? 'dest'
                          : 'src',
                      id             = convertReferenceSetId( !circularReferenceSetsHandled.includes( referenceSet.id )
                          ? referenceSet.id
                          : -referenceSet.id ),
                      targetItemType = targetItemIs === 'dest'
                          ? referenceSet.destItemType
                          : referenceSet.srcItemType;

                if ( referenceSet.srcItemTypeId === referenceSet.destItemTypeId && !circularReferenceSetsHandled.includes( referenceSet.id ) ) {
                    circularReferenceSetsHandled.push( referenceSet.id )
                }

                if (
                    (
                        !config.loadOnlyToOneReferences
                        || referenceSet.cardinality === '1:1'
                        || ( targetItemIs === 'dest' && referenceSet.cardinality === 'n:1' )
                        || ( targetItemIs === 'src' && referenceSet.cardinality === '1:n' )
                    )
                    && user.hasPermissionOnReferenceSet( 'read', referenceSet.id, schema )
                    && user.hasGenericPermissionOnReferenceSet( 'read', referenceSet.id, schema )
                ) {
                    const dataObject = getDataObject( String( id ), dataStructure );

                    if ( dataObject ) {
                        const columnDataDescriptor = buildDataDescriptor( { attributePath: `item.${targetItemType.displayAttribute}` }, dataObject ) as ObjectDataDescriptor;

                        retVal.push( {
                            key           : columnDataDescriptor.alias,
                            field         : columnDataDescriptor.alias,
                            header        : 'reference.type',
                            dataDescriptor: columnDataDescriptor,
                            permissions   : {},
                            editor        : false,
                            filter        : true,
                            width         : 300
                        } );
                    }
                }
            }
        }
    }

    return retVal;
}

/**
 * Creates the column definitions for properties
 *
 * @param {DataStructure} dataStructure
 * @param {String} schema
 * @param {String} itemTypeName
 * @param {User} user
 *
 * @return {ColumnDefinition[]}
 */
export const getPropertyColumnDefinitions = ( dataStructure : DataStructure, schema : Schema, itemTypeName : string, user : User ) : ColumnDefinition[] => {
    const retVal : ColumnDefinition[] = [];

    if ( itemTypeName !== '' ) {
        const propertySets = metaModel[ schema ].getPropertySetsForItemType( itemTypeName );

        if ( propertySets.length ) {

            // build columnDefinitions
            for ( const propertySet of propertySets ) {
                if (
                    !propertySet.isEnumerated
                    && user.hasPermissionOnPropertySet( 'read', propertySet.id, schema )
                    && user.hasGenericPermissionOnPropertySet( 'read', propertySet.id, schema )
                ) {
                    const columnDataDescriptor = buildDataDescriptor( { attributePath: `property.${propertySet.name}.value` }, dataStructure ) as ObjectDataDescriptor;

                    retVal.push( {
                        key           : columnDataDescriptor.alias,
                        field         : columnDataDescriptor.alias,
                        header        : 'property.type',
                        dataDescriptor: columnDataDescriptor,
                        permissions   : {},
                        editor        : true,
                        filter        : true,
                        width         : 300
                    } );

                }
            }
        }
    }

    return retVal;
}

/**
 * Creates the column definitions for enumerated properties
 *
 * @param {DataStructure} dataStructure
 * @param {String} schema
 * @param {String} itemTypeName
 * @param {User} user
 *
 * @return {ColumnDefinition[]}
 */
export const getEnumPropertyColumnDefinitions = ( dataStructure : DataStructure, schema : Schema, itemTypeName : string, user : User ) : ColumnDefinition[] => {
    const retVal : ColumnDefinition[] = [];

    if ( itemTypeName !== '' ) {
        const propertySets = metaModel[ schema ].getPropertySetsForItemType( itemTypeName );

        if ( propertySets.length ) {

            // build columnDefinitions
            for ( const propertySet of propertySets ) {
                if (
                    propertySet.isEnumerated
                    && user.hasPermissionOnPropertySet( 'read', propertySet.id, schema )
                    && user.hasGenericPermissionOnPropertySet( 'read', propertySet.id, schema )
                ) {
                    let columnDataDescriptor : DataDescriptor;

                    // orderNum
                    if ( propertySet.isKeyOrdered || propertySet.isValueOrdered ) {
                        columnDataDescriptor = buildDataDescriptor( { attributePath: `property.${propertySet.name}.orderNum` }, dataStructure ) as ObjectDataDescriptor;
                        retVal.push( {
                            key           : columnDataDescriptor.alias,
                            field         : columnDataDescriptor.alias,
                            header        : 'property.type.orderNum',
                            dataDescriptor: columnDataDescriptor,
                            permissions   : {},
                            editor        : true,
                            filter        : true,
                            width         : 300
                        } );
                    }

                    // key
                    if ( propertySet.isKeySettable ) {
                        columnDataDescriptor = buildDataDescriptor( { attributePath: `property.${propertySet.name}.key` }, dataStructure ) as ObjectDataDescriptor;
                        retVal.push( {
                            key           : columnDataDescriptor.alias,
                            field         : columnDataDescriptor.alias,
                            header        : 'property.type.key',
                            dataDescriptor: columnDataDescriptor,
                            permissions   : {},
                            editor        : true,
                            filter        : true,
                            width         : 300
                        } );
                    }

                    // value
                    if ( propertySet.isValueSettable ) {
                        columnDataDescriptor = buildDataDescriptor( { attributePath: `property.${propertySet.name}.value` }, dataStructure ) as ObjectDataDescriptor;
                        retVal.push( {
                            key           : columnDataDescriptor.alias,
                            field         : columnDataDescriptor.alias,
                            header        : 'property.type.value',
                            dataDescriptor: columnDataDescriptor,
                            permissions   : {},
                            editor        : true,
                            filter        : true,
                            width         : 300
                        } );
                    }
                }
            }
        }
    }

    return retVal;
}
